import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import SourceCombobox from '../common/SourceCombobox';
import { MT5AccountUser } from '../../utils/types';

interface TicketInformationProps {
  subject: string;
  category: string;
  priority: string;
  email: string;
  accountNumber?: string;
  onSubjectChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onPriorityChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onAccountNumberChange: (value: string) => void;
  errors?: {
    subject?: string;
    email?: string;
  };
  categories: ReadonlyArray<{ value: string; label: string }>;
  priorities: ReadonlyArray<{ value: string; label: string }>;
  mt5Accounts?: MT5AccountUser[];
  showMt5Dropdown?: boolean;
  loadingMt5Accounts?: boolean;
}

const TicketInformation = ({
  subject,
  category,
  priority,
  email,
  accountNumber,
  onSubjectChange,
  onCategoryChange,
  onPriorityChange,
  onEmailChange,
  onAccountNumberChange,
  errors = {},
  categories,
  priorities,
  mt5Accounts = [],
  showMt5Dropdown = false,
  loadingMt5Accounts = false,
}: TicketInformationProps) => {
  const selectedCategoryLabel =
    categories.find(option => option.value === category)?.label || category;
  const selectedPriorityLabel =
    priorities.find(option => option.value === priority)?.label || priority;

  const mt5AccountOptions = mt5Accounts.map(account => ({
    value: (account.mt5Login || account.Login || '').toString(),
    label: `${account.mt5Login || account.Login || ''} - ${account.name || account.Name || ''}`,
  }));

  const selectedMt5AccountLabel = mt5AccountOptions.find(
    option => option.value === accountNumber
  )?.label;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Ticket Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="subject">Subject *</Label>
          <Input
            id="subject"
            placeholder="Brief description of your issue"
            value={subject}
            onChange={e => onSubjectChange(e.target.value)}
            className={errors.subject ? 'border-red-500' : ''}
          />
          {errors.subject && (
            <p className="text-sm text-red-500">{errors.subject}</p>
          )}
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <SourceCombobox
              id="category"
              items={categories.map(option => option.label)}
              placeholder="Select a category"
              value={selectedCategoryLabel}
              onChange={value => {
                const option = categories.find(item => item.label === value);
                if (option) {
                  onCategoryChange(option.value);
                }
              }}
              emptyText="No categories found."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="priority">Priority</Label>
            <SourceCombobox
              id="priority"
              items={priorities.map(option => option.label)}
              placeholder="Select priority"
              value={selectedPriorityLabel}
              onChange={value => {
                const option = priorities.find(item => item.label === value);
                if (option) {
                  onPriorityChange(option.value);
                }
              }}
              emptyText="No priorities found."
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={e => onEmailChange(e.target.value)}
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="accountNumber">Account Number</Label>
            {showMt5Dropdown ? (
              loadingMt5Accounts ? (
                <Input
                  id="accountNumber"
                  placeholder="Loading trading accounts..."
                  disabled
                  value={accountNumber || ''}
                />
              ) : mt5AccountOptions.length > 0 ? (
                <SourceCombobox
                  id="accountNumber"
                  items={mt5AccountOptions.map(option => option.label)}
                  placeholder="Select trading account"
                  value={selectedMt5AccountLabel || ''}
                  onChange={value => {
                    const option = mt5AccountOptions.find(
                      item => item.label === value
                    );
                    if (option) {
                      onAccountNumberChange(option.value);
                    }
                  }}
                  emptyText="No trading accounts found."
                />
              ) : (
                <Input
                  id="accountNumber"
                  placeholder="e.g., DEM-00123456"
                  value={accountNumber || ''}
                  onChange={e => onAccountNumberChange(e.target.value)}
                />
              )
            ) : (
              <Input
                id="accountNumber"
                placeholder="e.g., DEM-00123456"
                value={accountNumber || ''}
                onChange={e => onAccountNumberChange(e.target.value)}
              />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TicketInformation;
